import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatasetSettingsRow from './DatasetSettingsRow.jsx';
import DatasetSizeSettings from './DatasetSizeSettings.jsx';
import Backend from 'react-dnd-html5-backend';
import { DragHandle, DropArea } from '../../../common/DragNDrop.jsx';
import { DndProvider } from 'react-dnd';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute.js';
import { useDatasetSettings } from '../../hooks/useDatasetSettings.js';
import { widgetsNoDatasetOutput } from '../../WidgetLibraryGen2.jsx';
import { cloneDeep } from 'lodash';
import styles from '../common/ListGroupItem.module.scss';
import Dialog from '../../../bootstrap/Dialog.jsx';
import { closeDetail, openDetail, saveWidgetSettings } from '../../redux/dashboardSlice.js';
import { FaIcon } from '../../../common/Icons.jsx';
import CollapsibleSection from '../../../common/CollapsibleSection.jsx';
import DetailSidebarSettings, { SIDEBAR_SETTING_TYPES } from '../DetailSidebarSettings.jsx';

function SettingsDialog({InnerComponent, ...props}) {
    return (
        <Dialog {...props}>
            {InnerComponent}
        </Dialog>
    )
}

const DEFAULT_DATASET_SETTINGS = ["dataset_id", "name_item", "value", "number_unit", "number_precision"];
const DND_ITEM_TYPE = "dataset-row";
const HOVER_THRESHOLD = 0.25;
// Could optimize more to separate datasets as an object with key of ids of different dataset
// the dataset settings change will re render the specific section that relate to that dataset, not the whole datasets array like current case.
// but with the cost of managing order inside the dataset object itself, possibly need some backend change as well.
function DatasetSettings({ 
    widgetId,
    dataset_settings = DEFAULT_DATASET_SETTINGS,
    dataset_params = {},
    hide_color_selection_dataset_mode = ["grouped"],
    hide_color_selection_column_options = ["stacked", "clustered"],
    hide_dataset_size_settings = false,
    DatasetSettingsRowComponent = DatasetSettingsRow,
    elementTitle = I18n.t('dataset'),
    sectionTitle = I18n.t('dataset_settings'),
    hide_buttons = false
}) {
    const dispatch = useDispatch();
    const widget_type = useSelector(state => state.dashboard.widgets[widgetId].widget_type)
    const {datasets: settingsDatasets} = useDatasetSettings({widget_type, widgetId, dataset_params})
    const setAttribute = useSetWidgetAttribute(widgetId);
    const columns = useSelector(state => state.dashboard.widgets[widgetId].settings?.columns) || 'single';
    const dataset_mode = useSelector(state => state.dashboard.widgets[widgetId].settings?.dataset_mode) || 'single';
    const dataset_type = useSelector(state => state.dashboard.widgets[widgetId].settings?.dataset_type) || 'single';
    const datasets = useSelector(state => state.dashboard.widgets[widgetId].settings?.datasets) || [];
    const wholeDatasets = useSelector(state => state.dashboard.widgets[widgetId].settings?.wholeDatasets) || [];
    const currentDatasets = widgetsNoDatasetOutput.includes(widget_type) ? wholeDatasets : datasets;
    const datasetsKey = widgetsNoDatasetOutput.includes(widget_type) ? 'wholeDatasets' : 'datasets';
    const showGroupedDatasets = columns != "single" || dataset_mode != "single" || dataset_type != "single";
    const selectedDatasetIds = currentDatasets.filter((d) => d.dataset_id).map((d) => d.dataset_id);
    const showIncompatibleDatasetsWarning =
      showGroupedDatasets && [...new Set(settingsDatasets.filter((d) => d.group_by && selectedDatasetIds.includes(d.value ?? d.uuid)).map((d) => d.group_by))].length > 1;
    const hideColorSelection = hide_color_selection_column_options?.includes(columns) || hide_color_selection_dataset_mode.includes(dataset_mode)
    const colWidth = Math.max(Math.floor(12 / dataset_settings.length), 2);
    const [sourceIndex, setSourceIndex] = useState(null);

    const toggleDetail = (index) => {
        if (index != null) {
            dispatch(openDetail(`${SIDEBAR_SETTING_TYPES.dataset}-${index}`));
        } else {
            dispatch(closeDetail());
        }
        setSourceIndex(index);
    }
    
    const saveSettings = async () => {
        await dispatch(saveWidgetSettings());
        toggleDetail(null);
    }

    const onClose = () => {
        toggleDetail(null);
    }

    const moveDataset = (draggedIndex, targetIndex) => {
        const selectedDatasets = cloneDeep(datasets);
        const [d] = selectedDatasets.splice(draggedIndex, 1);
        selectedDatasets.splice(targetIndex, 0, d);
        setAttribute('settings.datasets', selectedDatasets)
    };

    const removeDataset = (dataset_id) => {
        if (datasets.length > 1) {
            setAttribute('settings.datasets', datasets.filter(d => d.id !== dataset_id));
        }
    };
    // Somehow when adding dataset the scroll X happens
    const datasetElems = currentDatasets.map((d, index) => {
        if (dataset_mode == 'grouped' && index > 0) return null;
        return (
            <DropArea 
                key={d.id}
                accept={DND_ITEM_TYPE}
                item={{ id: d.id, index, type: DND_ITEM_TYPE }}
                onMove={moveDataset}
                hoverThreshold={HOVER_THRESHOLD}
                render={({ dropRef, dragRef, isDragging }) => (
                    <li ref={dropRef} className={`${isDragging ? "is-dragging" : ""} ${hide_buttons ? '' : 'widget-container'} list-group-item ${styles['list-group-item']}`}>
                        {!hide_buttons && <DragHandle sortHandleClass={styles['sort-handle']} dragRef={dragRef} />}
                        <div className={styles['list-group-item-content']}>
                            <div className={styles['list-group-item-description']}>
                                <h4 className='text-truncate'>{elementTitle} {d.id}</h4>
                                <p>{d?.label || d?.name_item || I18n.t('unnamed')}</p>
                            </div>
                        </div>
                        <div className={styles['list-group-item-actions']}>
                            <a onClick={() => toggleDetail(index)}>
                                <FaIcon type='cog' style={{color: 'black'}} />
                            </a>
                            {dataset_mode != 'grouped' && !hide_buttons && <a onClick={(e) => removeDataset(d.id)}>
                                <FaIcon type='trash' className='text-danger' />
                            </a>}
                        </div>
                    </li>
                )}
            />
        )
    }).filter(item => item !== null)

    return (
        <CollapsibleSection title={sectionTitle}>
            {
                sourceIndex !== null &&
                <DetailSidebarSettings saveSettings={saveSettings} onClose={onClose} sidebarId={`${SIDEBAR_SETTING_TYPES.dataset}-${sourceIndex}`}>
                    <h4>{`Edit ${currentDatasets[sourceIndex]?.label || currentDatasets[sourceIndex]?.name_item}`}</h4>
                    <DatasetSettingsRowComponent
                        d={currentDatasets[sourceIndex]}
                        index={sourceIndex}
                        {...{colWidth, showGroupedDatasets, hideColorSelection, datasetsKey, dataset_settings, widgetId, datasets}} settingsDatasets={settingsDatasets} 
                    />
                </DetailSidebarSettings>
            }
            {showIncompatibleDatasetsWarning ? <div className="alert alert-warning">{I18n.t("incompatible_datasets_warning")}</div> : null}
            <div className='list-group'>
                <DndProvider backend={Backend}>
                    {datasetElems}
                </DndProvider>
            </div>
            {!hide_dataset_size_settings && <DatasetSizeSettings widgetId={widgetId} />}
        </CollapsibleSection>
    );
}

export default DatasetSettings;
